import React, { useCallback, useContext, useState } from 'react';
import { Dialog } from '@material-ui/core';
import {
  ChamberMemberCategory,
  UserType,
  useAcceptChamberMemberAgreementForActiveCategoryAdvertisingBannerOfChamberMemberMutation,
  useAcceptChamberMemberAgreementForActiveMemberProfileAndPerksBannersOfChamberMemberMutation,
} from '../../../../graphql/types';
import { UserTypeContext } from '../../../../UserTypeContext';

// CMP Accelerator Member Dashboard (2) banner disclaimer (ACC:1691-1709, logic
// ACC:2738, 3104, 3352-3364): shown on the first upload click, then the upload
// opens for that slot. The accepted text version is stored, so a member accepts
// this text once even if they accepted the older Chamber Perks App text.
// CPA pages keep ChamberMemberMemberProfileAndPerksBannersAgreementModal.

export const CMP_BANNER_DISCLAIMER_VERSION = 'cmp-2026-10';

export type CmpBannerDisclaimerKind = 'featured' | 'category';

export const CMP_BANNER_DISCLAIMER_TEXT = {
  heading: 'Accept this Disclaimer to Upload Content',
  title: {
    featured: 'Advertising Disclaimer for Featured Member Banners',
    category: 'Advertising Disclaimer for Category Advertising Banners',
  } as Record<CmpBannerDisclaimerKind, string>,
  sections: [
    {
      title: '1. Content Submission',
      text: 'Members are responsible for submitting professional and appropriate advertising content. All banners must follow the required file formats and specifications to ensure proper display.',
    },
    {
      title: '2. Review and Approval',
      text: 'All submitted banners are subject to review and approval. We reserve the right to reject or remove any content that is inappropriate, offensive, or does not meet the required guidelines.',
    },
    {
      title: '3. Member Responsibility',
      text: 'Members are responsible for ensuring their advertising content complies with all applicable laws, copyright and trademark requirements, and advertising standards. By submitting content, the Member confirms they have the necessary rights and permissions to use all images, videos, logos, and other materials provided.',
    },
  ],
  accept: 'I Accept',
};

const GOLD = '#E1A731';
const GOLD_DARK = '#C6912A';

export const CmpBannerDisclaimerDialog = ({
  open, kind, onClose, onAccept,
}: {
  open: boolean;
  kind: CmpBannerDisclaimerKind;
  onClose: () => void;
  onAccept: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth={false}
    PaperProps={{ style: { background: '#E9E7E6', borderRadius: 16, width: 'min(920px, 94vw)', maxHeight: '90vh', margin: 0 } }}
    BackdropProps={{ style: { background: 'rgba(0,0,0,0.5)' } }}>
    <div data-testid="cmp-banner-disclaimer" style={{ padding: '22px 26px 26px', boxSizing: 'border-box', position: 'relative', display: 'flex',
      flexDirection: 'column', gap: 18, minHeight: 0, maxHeight: '90vh', fontFamily: "'DM Sans', sans-serif", color: '#000000' }}>
      <button type="button" aria-label="Close" onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16, width: 28, height: 28, borderRadius: '50%', background: '#ED1C24', color: '#FFFFFF',
        border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
      }}>✕</button>
      <div style={{ fontSize: 20, fontWeight: 600, textAlign: 'center', marginTop: 4, padding: '0 34px' }}>{CMP_BANNER_DISCLAIMER_TEXT.heading}</div>
      <div style={{ background: '#FFFFFF', border: '1.5px dotted #000000', borderRadius: 8, padding: '26px 32px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 24 }}>{CMP_BANNER_DISCLAIMER_TEXT.title[kind]}</div>
        {CMP_BANNER_DISCLAIMER_TEXT.sections.map((section, index) => (
          <React.Fragment key={section.title}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{section.title}</div>
            <p style={{ fontSize: 14, lineHeight: 1.7, margin: index === CMP_BANNER_DISCLAIMER_TEXT.sections.length - 1 ? 0 : '0 0 20px' }}>{section.text}</p>
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button type="button" onClick={onAccept} style={{
          background: GOLD, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '13px 110px', maxWidth: '100%', boxSizing: 'border-box',
          fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}
          onMouseEnter={(event) => { event.currentTarget.style.background = GOLD_DARK; }}
          onMouseLeave={(event) => { event.currentTarget.style.background = GOLD; }}>{CMP_BANNER_DISCLAIMER_TEXT.accept}</button>
      </div>
    </div>
  </Dialog>
);

/**
 * Upload gate for CMP member banner slots. `guard(action)` runs the action straight
 * away once this text version is accepted (or for staff), otherwise it shows the
 * disclaimer and runs the action after "I Accept".
 */
export const useCmpBannerDisclaimer = ({
  kind, acceptedVersion, categoryId, enabled = true,
}: {
  kind: CmpBannerDisclaimerKind;
  acceptedVersion: string | null | undefined;
  /** Required for category banners. */
  categoryId?: ChamberMemberCategory;
  enabled?: boolean;
}) => {
  const userType = useContext(UserTypeContext)?.state?.userType;
  const [acceptedNow, setAcceptedNow] = useState(false);
  const [pending, setPending] = useState<null | (() => void)>(null);
  const [acceptFeatured] = useAcceptChamberMemberAgreementForActiveMemberProfileAndPerksBannersOfChamberMemberMutation();
  const [acceptCategory] = useAcceptChamberMemberAgreementForActiveCategoryAdvertisingBannerOfChamberMemberMutation();

  const accepted = !enabled || userType !== UserType.ChamberMemberAdmin || acceptedNow
    || acceptedVersion === CMP_BANNER_DISCLAIMER_VERSION;

  const guard = useCallback((action: () => void) => {
    if (accepted) action();
    else setPending(() => action);
  }, [accepted]);

  const accept = useCallback(() => {
    const variables = { agreementVersion: CMP_BANNER_DISCLAIMER_VERSION };
    if (kind === 'category' && categoryId) {
      acceptCategory({ variables: { ...variables, categoryId }, refetchQueries: ['getActiveCategoryAdvertisingBannersForChamberMember'] });
    } else if (kind === 'featured') {
      acceptFeatured({ variables, refetchQueries: ['getUser', 'getChamberMember'] });
    }
    setAcceptedNow(true);
    const action = pending;
    setPending(null);
    if (action) action();
  }, [acceptCategory, acceptFeatured, categoryId, kind, pending]);

  const dialog = (
    <CmpBannerDisclaimerDialog open={!!pending} kind={kind} onClose={() => setPending(null)} onAccept={accept} />
  );

  return { accepted, guard, dialog };
};
